import { useRef, useCallback, useEffect } from 'react';

const DRAWER_WIDTH = 320;
const EDGE_ZONE = 30;
const COMMIT_PX = 15;
const COMMIT_RATIO = 1.5;       // horizontal/vertical to commit
const VELOCITY_THRESH = 0.5;    // px/ms
const POSITION_THRESH = 0.4;    // fraction of DRAWER_WIDTH
const SETTLE_MS = 300;
const SETTLE_TIMEOUT = 350;     // safety fallback for transitionend

export default function useDrawerGesture(isOpen, setIsOpen) {
  const drawerRef = useRef(null);
  const overlayRef = useRef(null);

  // Single mutable gesture state — never triggers React renders during drag
  const g = useRef({
    active: false,
    startX: 0,
    startY: 0,
    startTime: 0,
    lastX: 0,
    committed: false,
    settling: false,
    openAtStart: false,
  });

  // Keep a ref-copy of isOpen so event handlers always see the latest value
  const isOpenRef = useRef(isOpen);
  useEffect(() => { isOpenRef.current = isOpen; }, [isOpen]);

  // ---- DOM helpers ----

  const applyPosition = useCallback((offsetX) => {
    // offsetX: 0 = fully closed (-320px), DRAWER_WIDTH = fully open (0px)
    const tx = offsetX - DRAWER_WIDTH;                        // range: -320 → 0
    const opacity = Math.max(0, Math.min(0.5, (offsetX / DRAWER_WIDTH) * 0.5));

    if (drawerRef.current) {
      drawerRef.current.style.transform = `translateX(${tx}px)`;
    }
    if (overlayRef.current) {
      overlayRef.current.style.opacity = opacity;
      overlayRef.current.style.pointerEvents = opacity > 0 ? 'auto' : 'none';
    }
  }, []);

  const settle = useCallback((toOpen) => {
    const gs = g.current;
    gs.settling = true;

    const drawer = drawerRef.current;
    const overlay = overlayRef.current;
    const transition = `transform ${SETTLE_MS}ms cubic-bezier(0.4,0,0.2,1)`;
    const overlayTransition = `opacity ${SETTLE_MS}ms cubic-bezier(0.4,0,0.2,1)`;

    if (drawer) {
      drawer.style.transition = transition;
      drawer.style.transform = toOpen ? 'translateX(0px)' : `translateX(-${DRAWER_WIDTH}px)`;
    }
    if (overlay) {
      overlay.style.transition = overlayTransition;
      overlay.style.opacity = toOpen ? 0.5 : 0;
      overlay.style.pointerEvents = toOpen ? 'auto' : 'none';
    }

    const cleanup = () => {
      if (drawer) drawer.style.transition = '';
      if (overlay) overlay.style.transition = '';
      gs.settling = false;
      setIsOpen(toOpen);
    };

    // Listen for transitionend, with safety timeout
    let done = false;
    const onEnd = () => {
      if (done) return;
      done = true;
      clearTimeout(timer);
      cleanup();
    };
    const timer = setTimeout(onEnd, SETTLE_TIMEOUT);
    if (drawer) {
      drawer.addEventListener('transitionend', onEnd, { once: true });
    } else {
      // No drawer ref — just fire cleanup
      clearTimeout(timer);
      cleanup();
    }
  }, [setIsOpen]);

  // ---- Imperative API ----

  const open = useCallback(() => {
    if (g.current.settling) return;
    settle(true);
  }, [settle]);

  const close = useCallback(() => {
    if (g.current.settling) return;
    settle(false);
  }, [settle]);

  const toggle = useCallback(() => {
    if (g.current.settling) return;
    settle(!isOpenRef.current);
  }, [settle]);

  // ---- Touch handlers ----

  const onTouchStart = useCallback((e) => {
    const gs = g.current;
    if (gs.settling) return;

    // Skip devices with fine pointer (laptop touchscreens)
    if (window.matchMedia('(pointer: fine)').matches && 'ontouchstart' in window) return;

    const touch = e.touches[0];
    const x = touch.clientX;

    // Skip touches in the bottom input area or on textarea
    if (touch.clientY > window.innerHeight - 200 || e.target.closest('textarea')) return;

    const currentOpen = isOpenRef.current;

    if (!currentOpen && x >= EDGE_ZONE) return; // closed: only near left edge

    gs.active = true;
    gs.startX = x;
    gs.startY = touch.clientY;
    gs.startTime = Date.now();
    gs.lastX = x;
    gs.committed = false;
    gs.openAtStart = currentOpen;
  }, []);

  const onTouchMove = useCallback((e) => {
    const gs = g.current;
    if (!gs.active) return;

    const touch = e.touches[0];
    const x = touch.clientX;
    const dx = x - gs.startX;
    const dy = touch.clientY - gs.startY;
    gs.lastX = x;

    // Commit check
    if (!gs.committed) {
      if (Math.abs(dx) < COMMIT_PX) return;  // not enough movement yet
      if (Math.abs(dx) < Math.abs(dy) * COMMIT_RATIO) {
        // vertical scroll wins → cancel gesture
        gs.active = false;
        return;
      }
      // Correct direction?
      if (gs.openAtStart && dx > 0) { gs.active = false; return; } // open + rightward: ignore
      if (!gs.openAtStart && dx < 0) { gs.active = false; return; } // closed + leftward: ignore
      gs.committed = true;
    }

    e.preventDefault();

    // Compute drawer offset (0 = closed, DRAWER_WIDTH = open)
    let offsetX;
    if (gs.openAtStart) {
      // closing: start at DRAWER_WIDTH, add negative dx
      offsetX = Math.max(0, Math.min(DRAWER_WIDTH, DRAWER_WIDTH + dx));
    } else {
      // opening: start at 0, add positive dx
      offsetX = Math.max(0, Math.min(DRAWER_WIDTH, dx));
    }

    applyPosition(offsetX);
  }, [applyPosition]);

  const onTouchEnd = useCallback(() => {
    const gs = g.current;
    if (!gs.active) return;
    gs.active = false;

    if (!gs.committed) return; // no drag happened

    const dx = gs.lastX - gs.startX;
    const dt = Date.now() - gs.startTime;
    const velocity = Math.abs(dx) / (dt || 1);

    let toOpen;
    if (velocity > VELOCITY_THRESH) {
      // Fast flick → snap in swipe direction
      toOpen = dx > 0;
    } else {
      // Position-based
      let offsetX;
      if (gs.openAtStart) {
        offsetX = Math.max(0, Math.min(DRAWER_WIDTH, DRAWER_WIDTH + dx));
      } else {
        offsetX = Math.max(0, Math.min(DRAWER_WIDTH, dx));
      }
      toOpen = offsetX > DRAWER_WIDTH * POSITION_THRESH;
    }

    settle(toOpen);
  }, [settle]);

  const handlers = { onTouchStart, onTouchMove, onTouchEnd };

  // ---- Sync external isOpen changes to DOM (desktop resize, programmatic) ----

  useEffect(() => {
    const gs = g.current;
    if (gs.active || gs.settling) return; // don't fight a gesture in progress
    // Snap immediately — no animation
    applyPosition(isOpen ? DRAWER_WIDTH : 0);
  }, [isOpen, applyPosition]);

  return { drawerRef, overlayRef, handlers, open, close, toggle };
}
